import React, { useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { DictionaryWord } from 'app/types';
import { ResolvedTableSettings, TableColumnKey } from 'components/tableSettings';
import AddWordPanel from './AddWordPanel';
import FilterBar from './FilterBar';
import ManagerTable from './ManagerTable';
import WorkCards from './WorkCards';
import SettingsPopover from './SettingsPopover';
import UndoToast from './UndoToast';
import { WordStatus } from './meta';
import { UseVocabularyWordsViewState } from './useVocabularyWordsView';

export interface VocabularyWordsViewProps {
  words: DictionaryWord[];
  partOfSpeechOptions: string[];

  view: UseVocabularyWordsViewState;
  tableSettings: ResolvedTableSettings;
  onSaveTableSettings: (next: ResolvedTableSettings) => Promise<void> | void;
  onResetTableSettings: () => Promise<void> | void;

  onAddWord?: (payload: {
    word: string;
    transcription: string;
    translation: string;
    partOfSpeech: string;
    definition: string;
  }) => void;

  /** Single-row status setter (wired to `vocabularyWordsUpdate` with enum). */
  onSetStatus: (id: number, status: WordStatus) => void;
  /** Single-row delete (kept for the optional confirm dialog flow). */
  onDeleteOne: (id: number) => void;
  /** Bulk operations from the selection bar. */
  onBulkSetStatus: (status: WordStatus) => void;
  onBulkDelete: () => void;
  /** Undo toast — message goes nullable when dismissed. */
  undoMessage?: string | null;
  onUndo?: () => void;
  onUndoDismiss?: () => void;

  /** Pagination range label "X–Y з N слів". When false, render no footer. */
  paginationFooter?: React.ReactNode;

  /** Optional: render an Alert (errors, etc.) above the content. */
  alert?: React.ReactNode;
}

const CARD_FIELD_KEYS: TableColumnKey[] = [
  'transcription',
  'partOfSpeech',
  'definition',
];

const VocabularyWordsView: React.FC<VocabularyWordsViewProps> = ({
  partOfSpeechOptions,
  view,
  tableSettings,
  onSaveTableSettings,
  onResetTableSettings,
  onAddWord,
  onSetStatus,
  onDeleteOne,
  onBulkSetStatus,
  onBulkDelete,
  undoMessage,
  onUndo,
  onUndoDismiss,
  paginationFooter,
  alert,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const settingsAnchorRef = useRef<HTMLButtonElement | null>(null);

  // On phone we always render the cards mode; the toggle is hidden anyway.
  const renderMode: 'cards' | 'table' = isPhone ? 'cards' : view.viewMode;

  return (
    <>
      {alert && <Box sx={{ mb: 2 }}>{alert}</Box>}

      {view.addOpen && (
        <AddWordPanel
          partOfSpeechOptions={partOfSpeechOptions}
          onCancel={() => view.setAddOpen(false)}
          onAdd={(payload) => {
            onAddWord?.(payload);
            view.setAddOpen(false);
          }}
        />
      )}

      <FilterBar
        view={view}
        partOfSpeechOptions={partOfSpeechOptions}
        onBulkSetStatus={onBulkSetStatus}
        onBulkDelete={onBulkDelete}
        settingsAnchorRef={settingsAnchorRef}
        onToggleSettings={() => view.setSettingsOpen(!view.settingsOpen)}
        settingsOpen={view.settingsOpen}
      />

      {renderMode === 'table' ? (
        <ManagerTable
          view={view}
          tableSettings={tableSettings}
          onSetStatus={onSetStatus}
          onDeleteOne={onDeleteOne}
        />
      ) : (
        <WorkCards
          view={view}
          tableSettings={tableSettings}
          onSetStatus={onSetStatus}
          onDeleteOne={onDeleteOne}
        />
      )}

      {paginationFooter && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            mt: 2.5,
            alignItems: 'center',
          }}
        >
          {paginationFooter}
        </Box>
      )}

      <SettingsPopover
        anchorEl={settingsAnchorRef.current}
        open={view.settingsOpen}
        onClose={() => view.setSettingsOpen(false)}
        tableSettings={tableSettings}
        onSaveTableSettings={onSaveTableSettings}
        onResetTableSettings={onResetTableSettings}
        view={view}
        cardFieldKeys={CARD_FIELD_KEYS}
      />

      <UndoToast
        message={undoMessage ?? null}
        onUndo={onUndo}
        onDismiss={onUndoDismiss}
      />
    </>
  );
};

export default VocabularyWordsView;
